import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';

export interface FeatureGuideStep {
  id: string;
  title: string;
  description: string;
  target?: string;
  /** Extra space reserved at the bottom for fixed UI such as action bars. */
  bottomSafeArea?: number;
}

interface FeatureGuideOverlayProps {
  steps: FeatureGuideStep[];
  onComplete?: (stepId: string) => void;
}

interface TargetShape {
  topLeft: number;
  topRight: number;
  bottomRight: number;
  bottomLeft: number;
}

const getRadiusInPixels = (value: string, size: number): number => {
  const numericValue = Number.parseFloat(value) || 0;
  return value.includes('%') ? (numericValue / 100) * size : numericValue;
};

const buildRoundedRectPath = (rect: DOMRect, shape: TargetShape): string => {
  const topLeft = Math.min(shape.topLeft, rect.width / 2, rect.height / 2);
  const topRight = Math.min(shape.topRight, rect.width / 2, rect.height / 2);
  const bottomRight = Math.min(shape.bottomRight, rect.width / 2, rect.height / 2);
  const bottomLeft = Math.min(shape.bottomLeft, rect.width / 2, rect.height / 2);
  const right = rect.right;
  const bottom = rect.bottom;

  return [
    `M ${rect.left + topLeft} ${rect.top}`,
    `H ${right - topRight}`,
    `Q ${right} ${rect.top} ${right} ${rect.top + topRight}`,
    `V ${bottom - bottomRight}`,
    `Q ${right} ${bottom} ${right - bottomRight} ${bottom}`,
    `H ${rect.left + bottomLeft}`,
    `Q ${rect.left} ${bottom} ${rect.left} ${bottom - bottomLeft}`,
    `V ${rect.top + topLeft}`,
    `Q ${rect.left} ${rect.top} ${rect.left + topLeft} ${rect.top}`,
    'Z',
  ].join(' ');
};

const getCompletedSteps = (key: string): string[] => {
  if (typeof window === 'undefined') return [];

  try {
    const value = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
};

const FEATURE_GUIDES_STORAGE_KEY = 'booka_feature_guides';
const LEGACY_FEATURE_GUIDE_PREFIX = 'booka_feature_guide_';

const getLegacyFeatureGuideKeys = (): string[] => {
  if (typeof window === 'undefined') return [];

  try {
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(LEGACY_FEATURE_GUIDE_PREFIX)) keys.push(key);
    }
    return keys;
  } catch {
    return [];
  }
};

const getAllCompletedGuideSteps = (): string[] => {
  if (typeof window === 'undefined') return [];

  try {
    const completed = new Set(getCompletedSteps(FEATURE_GUIDES_STORAGE_KEY));

    // Preserve completion state from the previous per-guide storage keys.
    // This migration is read-only for legacy keys and writes only to the
    // single central registry below.
    getLegacyFeatureGuideKeys().forEach((key) => {
      getCompletedSteps(key).forEach((stepId) => completed.add(stepId));
    });

    return Array.from(completed);
  } catch {
    return [];
  }
};

const FeatureGuideOverlay = ({
  steps,
  onComplete,
}: FeatureGuideOverlayProps) => {
  const [completedSteps, setCompletedSteps] = useState<string[]>(getAllCompletedGuideSteps);
  const [stepIndex, setStepIndex] = useState(() => {
    const completed = getAllCompletedGuideSteps();
    return steps.findIndex((step) => !completed.includes(step.id));
  });
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [targetShape, setTargetShape] = useState<TargetShape | null>(null);
  const [guidePosition, setGuidePosition] = useState<{ top: number; left: number } | null>(null);
  const guideGroupRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (steps.length === 0) return;

    const completed = getAllCompletedGuideSteps();
    setCompletedSteps(completed);
    const legacyKeys = getLegacyFeatureGuideKeys();
    if (legacyKeys.length > 0) {
      try {
        window.localStorage.setItem(FEATURE_GUIDES_STORAGE_KEY, JSON.stringify(completed));
        legacyKeys.forEach((key) => {
          window.localStorage.removeItem(key);
        });
      } catch {
        // A blocked localStorage must not prevent the guide from rendering.
      }
    }
    const firstPendingIndex = steps.findIndex((step) => !completed.includes(step.id));
    setStepIndex(firstPendingIndex);
  }, [steps]);

  const step = steps[stepIndex];

  useEffect(() => {
    if (!step?.target) {
      setTargetRect(null);
      setTargetShape(null);
      return;
    }

    const updateTargetRect = () => {
      const target = document.querySelector<HTMLElement>(step.target!);
      if (!target) {
        setTargetRect(null);
        setTargetShape(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      const visibleBottom = window.innerHeight - (step.bottomSafeArea ?? 0);
      // Wait until the complete target is inside the viewport. Showing the
      // guide while only part of a heading is visible can lock scrolling and
      // leave the user unable to see the highlighted feature.
      const isFullyVisible =
        rect.top >= 0 &&
        rect.bottom <= visibleBottom &&
        rect.left >= 0 &&
        rect.right <= window.innerWidth;
      if (!isFullyVisible) {
        setTargetRect(null);
        setTargetShape(null);
        return;
      }

      const computedStyle = window.getComputedStyle(target);
      setTargetRect(rect);
      setTargetShape({
        topLeft: getRadiusInPixels(computedStyle.borderTopLeftRadius, rect.width),
        topRight: getRadiusInPixels(computedStyle.borderTopRightRadius, rect.width),
        bottomRight: getRadiusInPixels(computedStyle.borderBottomRightRadius, rect.width),
        bottomLeft: getRadiusInPixels(computedStyle.borderBottomLeftRadius, rect.width),
      });
    };

    updateTargetRect();
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(updateTargetRect);
    });
    const settleInterval = window.setInterval(updateTargetRect, 50);
    const settleTimer = window.setTimeout(() => {
      window.clearInterval(settleInterval);
    }, 700);
    const mutationObserver = new MutationObserver(() => {
      updateTargetRect();
    });
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
    window.addEventListener('resize', updateTargetRect);
    window.addEventListener('scroll', updateTargetRect, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearInterval(settleInterval);
      window.clearTimeout(settleTimer);
      mutationObserver.disconnect();
      window.removeEventListener('resize', updateTargetRect);
      window.removeEventListener('scroll', updateTargetRect, true);
    };
  }, [step?.target]);

  useEffect(() => {
    if (!targetRect) {
      setGuidePosition(null);
      return;
    }

    const positionGuide = () => {
      const guideRect = guideGroupRef.current?.getBoundingClientRect();
      if (!guideRect) return;

      const viewportPadding = 16;
      const gap = 14;
      const aboveTop = targetRect.top - guideRect.height - gap;
      const belowTop = targetRect.bottom + gap;
      const maxTop = window.innerHeight - guideRect.height - viewportPadding;
      const top = aboveTop >= viewportPadding
        ? aboveTop
        : belowTop <= maxTop
          ? belowTop
          : Math.max(viewportPadding, Math.min(aboveTop, maxTop));
      const maxLeft = window.innerWidth - guideRect.width - viewportPadding;
      const targetAlignedLeft = targetRect.right - guideRect.width;
      const left = Math.max(viewportPadding, Math.min(targetAlignedLeft, maxLeft));

      setGuidePosition({
        top: Math.max(viewportPadding, Math.min(top, Math.max(viewportPadding, maxTop))),
        left,
      });
    };

    const frame = window.requestAnimationFrame(positionGuide);
    window.addEventListener('resize', positionGuide);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', positionGuide);
    };
  }, [targetRect]);

  useEffect(() => {
    if (!targetRect) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousDocumentOverflow = document.documentElement.style.overflow;
    const previousBodyTouchAction = document.body.style.touchAction;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousDocumentOverflow;
      document.body.style.touchAction = previousBodyTouchAction;
    };
  }, [targetRect]);

  if (steps.length === 0 || !step || stepIndex < 0 || stepIndex >= steps.length) return null;
  if (step.target && !targetRect) return null;

  const spotlightPath = targetRect && targetShape
    ? buildRoundedRectPath(targetRect, targetShape)
    : null;
  const spotlightMaskId = `feature-guide-mask-${step.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const spotlightFilterId = `feature-guide-filter-${step.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const guideGroupStyle = targetRect
    ? guidePosition
      ? { top: `${guidePosition.top}px`, left: `${guidePosition.left}px` }
      : { top: '16px', left: '16px' }
    : undefined;

  const handleUnderstood = () => {
    // Read the latest registry before writing so a concurrently mounted guide
    // cannot overwrite completions made by another guide with stale state.
    const latestCompleted = getAllCompletedGuideSteps();
    const nextCompleted = latestCompleted.includes(step.id)
      ? latestCompleted
      : [...latestCompleted, step.id];

    setCompletedSteps(nextCompleted);
    try {
      window.localStorage.setItem(FEATURE_GUIDES_STORAGE_KEY, JSON.stringify(nextCompleted));
    } catch {
      // A blocked localStorage must not prevent the user from continuing.
    }
    onComplete?.(step.id);

    const nextPendingIndex = steps.findIndex(
      (candidate, index) => index > stepIndex && !nextCompleted.includes(candidate.id),
    );
    setStepIndex(nextPendingIndex >= 0 ? nextPendingIndex : steps.length);
  };

  if (typeof document === 'undefined') return null;

  return createPortal((
    <motion.div
      className="fixed inset-0 z-[200] flex items-end justify-center px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-8"
      dir="rtl"
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      {!spotlightPath && <div className="pointer-events-none fixed inset-0 bg-slate-950/60 backdrop-blur-[3px]" />}

      {spotlightPath && (
        <svg
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 h-full w-full overflow-visible"
          width="100%"
          height="100%"
          viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`}
          preserveAspectRatio="none"
        >
          <defs>
            <filter id={spotlightFilterId} x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="7" />
            </filter>
            <mask id={spotlightMaskId} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse" x="0" y="0" width="100%" height="100%">
              <rect width="100%" height="100%" fill="white" />
              <path d={spotlightPath} fill="black" filter={`url(#${spotlightFilterId})`} />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(15, 23, 42, 0.60)" mask={`url(#${spotlightMaskId})`} />
        </svg>
      )}

      <motion.div
        ref={guideGroupRef}
        className={`${targetRect ? 'fixed z-[202]' : 'relative z-[202]'} flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-md flex-row items-end gap-2 overflow-visible sm:gap-3`}
        style={guideGroupStyle}
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 360, damping: 28 }}
      >
        <div className="flex h-28 w-24 shrink-0 items-end justify-center sm:h-36 sm:w-32">
          <motion.img
            src="/character.webp"
            alt="شخصیت راهنما"
            className="h-full w-full object-contain"
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        <section
        role="dialog"
        aria-modal="true"
        aria-label="راهنمای مسیر مطالعه کتاب"
        aria-describedby="feature-guide-description"
        className="relative w-full rounded-[26px] border border-white bg-white px-5 pb-5 pt-5 text-black shadow-[0_16px_45px_rgba(0,0,0,0.28)] sm:px-6"
      >
        <span
          aria-hidden="true"
          className="absolute -right-3 bottom-8 h-6 w-6 rotate-45 border-r border-t border-white bg-white shadow-[5px_-5px_10px_-8px_rgba(0,0,0,0.35)]"
        />
        <div className="relative z-10">
          <p
            id="feature-guide-description"
            className="text-justify text-xs leading-7 text-black sm:text-sm"
          >
            {step.description}
          </p>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              autoFocus
              onClick={handleUnderstood}
              className="flex w-full items-center justify-center rounded-lg bg-[#343147] px-3 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#403b58] focus:outline-none focus:ring-2 focus:ring-[#343147]/30"
            >
              فهمیدم
            </button>
          </div>
        </div>
      </section>
      </motion.div>
    </motion.div>
  ), document.body);
};

export default FeatureGuideOverlay;
